// app/frontend/components/FlashNotificationHandler.tsx
import { useEffect } from 'react';
import { usePage } from '@inertiajs/react';
import { showNotification } from '@mantine/notifications';
import { PageProps } from '@/types';

const SEEN_KEY = 'wcwd:flash-seen';
const SEEN_LIMIT = 20;

// Ids of flashes already toasted. Inertia restores old props on back/forward,
// so the same flash comes round again; the server gives each one an id (#2001).
// sessionStorage keeps the list across a full reload; the in-memory copy
// covers a private window where storage throws.
let memorySeen: string[] = [];

function readSeen(): string[] {
  try {
    return JSON.parse(window.sessionStorage.getItem(SEEN_KEY) || '[]');
  } catch {
    return memorySeen;
  }
}

function markSeen(id: string) {
  const seen = [...readSeen(), id].slice(-SEEN_LIMIT);
  memorySeen = seen;
  try {
    window.sessionStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    // memorySeen already holds it
  }
}

/**
 * Global component that turns Rails flash messages into toasts.
 * ApplicationController shares the flash as props.flash on every Inertia page.
 */
export default function FlashNotificationHandler() {
  const { props } = usePage<PageProps>();
  const flash = props.flash;

  useEffect(() => {
    if (!flash) return;
    if (flash.id) {
      if (readSeen().includes(flash.id)) return;
      markSeen(flash.id);
    }

    // Handle notice (success messages)
    if (flash.notice) {
      showNotification({
        title: 'Success',
        message: flash.notice,
        color: 'green',
      });
    }

    // Handle alert (error messages)
    if (flash.alert) {
      showNotification({
        title: 'Error',
        message: flash.alert,
        color: 'red',
      });
    }

    // Handle success (alternative success format)
    if (flash.success) {
      showNotification({
        title: 'Success',
        message: flash.success,
        color: 'green',
      });
    }

    // Handle error (alternative error format)
    if (flash.error) {
      showNotification({
        title: 'Error',
        message: flash.error,
        color: 'red',
      });
    }

    // Handle toast (custom toast messages with title, body, icon, delay)
    if (flash.toast) {
      showNotification({
        title: flash.toast.title || 'Notification',
        message: flash.toast.body || '',
        color: 'blue',
        autoClose: flash.toast.delay || 5000,
      });
    }
  }, [flash]);

  // This component doesn't render anything
  return null;
}
